import React, { useContext, useEffect, useState } from "react";
import { EmailConfigModel } from "./_core/_model.ts";
import { GetEmailConfig, UpsertEmailConfig } from "./_core/_request.ts";
import { AuthContext } from "../../context/AuthContext.js";
import { useFormik } from 'formik';
import * as Yup from 'yup';
import SavingIndicator from "../../UIComponents/SavingIndicator.tsx";
import { defaultTheme } from "../../styles/themes/defaultTheme.js";
import { AnimatePresence } from "framer-motion";
import { GetObjectDifferences } from "../../Utilities/Utilities.ts";
import PasswordInput from "../Tools/PasswordInput.tsx";

export function EmailConfig() {
    const { user } = useContext(AuthContext);
    const [progress, setProgress] = useState(0);
    const [formData, setFormData] = useState<EmailConfigModel>({
        emailConfigID: 0,
        from: "",
        username: "",
        password: "",
        host: "",
        port: 0,
        defaultCredentials: false,
        enableSsl: false,
        createdBy: user.userID,
        createdAt: null,
        dirtyFields: []
    });
    const validationSchema = Yup.object({
        from: Yup.string().email('Invalid email address').required('This field is required'),
        username: Yup.string().min(6, 'Minimum 6 characters').required('This field is required'),
        password: Yup.string().min(6, 'Minimum 6 characters').required('This field is required'),
        host: Yup.string().min(6, 'Minimum 6 characters').required('This field is required'),
        port: Yup.number().min(6, 'Minimum 4 characters').required('This field is required'),
    });
    const formik = useFormik({
        validationSchema,
        initialValues: formData,
        enableReinitialize: true,
        onSubmit: (values) => {
            setProgress(1);
            values.dirtyFields = GetObjectDifferences(values, formData);
            values.dirtyFields.splice(values.dirtyFields.indexOf("dirtyFields"), 1);
            UpsertEmailConfig(values).then((x) => {
                setProgress(0);
            })
        },
    });

    const reInit = () => {
        setProgress(-1);
        GetEmailConfig(0).then((x) => {
            let newValues: EmailConfigModel = {
                emailConfigID: 0,
                from: "",
                username: "",
                password: "",
                host: "",
                port: 0,
                defaultCredentials: false,
                enableSsl: false,
                createdBy: user.userID,
                createdAt: null,
                dirtyFields: []
            };
            if (x.data.length > 0)
                newValues = x.data[0];
            setFormData(newValues);
            formik.resetForm({ values: newValues });
            setProgress(0);
        })
    }
    useEffect(() => {
        reInit();
    }, [])
    return (
        <div className="row mb-4 mx-0 px-2 py-2 border rounded-3">
            <form onSubmit={formik.handleSubmit} style={{ margin: 'auto' }}>
                <div className="col-md-12 mb-3  j_st_eC_frm">
                    <div className="col-md-12 d-flex flex-row justify-content-between">
                        <label className="form-label fw-bold">From Email Address</label>
                        <div className=" d-flex flex-row align-items-center gap-5">
                            <div className="j_st_eC_dCred">
                                <label className="form-label fw-bold ">Default Credentials</label>
                                <input type="checkbox" onChange={formik.handleChange}
                                    checked={formik.values.defaultCredentials} name="defaultCredentials" className="form-check-input" style={{ margin: "0px 0px 0px 5px" }} />
                            </div>
                            <div className="j_st_eC_ssl">
                                <label className="form-label fw-bold">Enable SSL</label>
                                <input type="checkbox" onChange={formik.handleChange}
                                    checked={formik.values.enableSsl} name="enableSsl" className="form-check-input" style={{ margin: "0px 0px 0px 5px" }} />
                            </div>
                        </div>
                    </div>
                    <input type="email" name="from" onChange={formik.handleChange}
                        value={formik.values.from} onBlur={formik.handleBlur} className={`${formik.touched.from && formik.errors.from ? "is-invalid" : ""} form-control`} />
                    <div className="invalid-feedback">
                        {
                            formik.errors.from
                        }
                    </div>
                </div>
                <div className="col-md-12 mb-3 j_st_eC_unme">
                    <label className="form-label fw-bold">User Name</label>
                    <input type="text" name="username"
                        value={formik.values.username} onChange={formik.handleChange}
                        onBlur={formik.handleBlur} className={`${formik.touched.username && formik.errors.username ? "is-invalid" : ""} form-control`} />
                    <div className="invalid-feedback">
                        {
                            formik.errors.username
                        }
                    </div>
                </div>
                <div className="col-md-12 mb-3 j_st_eC_pwrd">
                    <label className="form-label fw-bold">Password</label>
                    <PasswordInput name="password"
                        readOnly={false}
                        value={formik.values.password}
                        onChange={formik.handleChange} />
                    <div className="invalid-feedback">
                        {
                            formik.errors.password
                        }
                    </div>
                </div>
                <div className="col-md-12 mb-3 j_st_eC_host">
                    <label className="form-label fw-bold">Host</label>
                    <input type="text" name="host" onChange={formik.handleChange}
                        value={formik.values.host} onBlur={formik.handleBlur} className={`${formik.touched.host && formik.errors.host ? "is-invalid" : ""} form-control`} />
                    <div className="invalid-feedback">
                        {
                            formik.errors.host
                        }
                    </div>
                </div>
                <div className="col-md-12 mb-3 j_st_eC_port">
                    <label className="form-label fw-bold">Port</label>
                    <input type="number" name="port" onChange={formik.handleChange}
                        value={formik.values.port} onBlur={formik.handleBlur} className={`${formik.touched.port && formik.errors.port ? "is-invalid" : ""} form-control`} />
                    <div className="invalid-feedback">
                        {
                            formik.errors.port
                        }
                    </div>
                </div>
                <div className="col-md-12 d-flex flex-row justify-content-end gap-2">
                    {
                        progress == -1 ?
                            <div className="border rounded-1">
                                <SavingIndicator {...{ color: defaultTheme.colors.primary, style: "pulse", message: "Loading", type: "saving" }} />
                            </div> :
                            <button className="btn btn-outline-primary j_st_eC_rst" type="button" onClick={reInit}>Reset</button>
                    }
                    <AnimatePresence>
                        {
                            progress == 1 ?
                                <div className="border rounded-1">
                                    <SavingIndicator {...{ color: defaultTheme.colors.primary, style: "spinner", message: "Saving", type: "saving" }} />
                                </div>
                                : <button className="btn btn-primary j_st_eC_save" type="submit">
                                    Save
                                </button>

                        }
                    </AnimatePresence>
                </div>
            </form>
        </div>
    );
}

export default EmailConfig